/**
 * VVS Fluid Background v2
 * ───────────────────────
 * Enhanced particle field with:
 * - Scroll velocity awareness
 * - Mouse tilt effect
 * - State transitions between sections
 * - Deeper wave motion
 */

import { useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion, isMobile, getIntensity } from '../motion/tokens';

gsap.registerPlugin(ScrollTrigger);

// Particle colors - deep blue + teal + cyan
const COLORS = [
  { r: 0, g: 140, b: 220 },   // Blue principal
  { r: 0, g: 80, b: 160 },    // Blue profond
  { r: 100, g: 180, b: 255 }, // Cyan léger
  { r: 0, g: 100, b: 180 },   // Blue intermédiaire
  { r: 0, g: 160, b: 140 },   // Teal
  { r: 0, g: 120, b: 130 },   // Deep teal
  { r: 0, g: 200, b: 220 },   // Cyan vif (accent)
];

export default function FluidBackgroundV2() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -1000, y: -1000, targetX: 0, targetY: 0 });
  const scrollRef = useRef({ velocity: 0, y: 0 });
  const rafRef = useRef(null);
  const timeRef = useRef(0);
  const intensityRef = useRef(1);
  
  // Update particle intensity (for section state transitions)
  const updateIntensity = useCallback((newIntensity) => {
    gsap.to(intensityRef, {
      current: newIntensity,
      duration: 1.5,
      ease: "power2.inOut",
    });
  }, []);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Skip on mobile or reduced motion
    const mobile = isMobile();
    const reducedMotion = prefersReducedMotion();
    
    if (reducedMotion) {
      // Just draw static gradient background
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      return;
    }
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      if (!mobile) {
        initParticles();
      }
    };
    
    // Initialize particles
    const initParticles = () => {
      // More particles for desktop, calculated based on screen size
      const baseCount = mobile ? 0 : 550;
      const numParticles = Math.min(baseCount, Math.floor((canvas.width * canvas.height) / 3000));
      particlesRef.current = [];
      
      for (let i = 0; i < numParticles; i++) {
        const color = COLORS[Math.floor(Math.random() * COLORS.length)];
        const depth = Math.random(); // 0 = far, 1 = close
        
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          baseX: 0,
          baseY: 0,
          vx: (Math.random() - 0.5) * 0.2,
          vy: (Math.random() - 0.5) * 0.2,
          size: 1 + depth * 2.5, // 1-3.5px based on depth
          opacity: 0.08 + depth * 0.18, // Closer = more visible
          color: color,
          offset: Math.random() * Math.PI * 2,
          depth: depth, // For parallax
          waveAmplitude: 0.3 + Math.random() * 0.4,
          waveSpeed: 0.0006 + Math.random() * 0.0004,
        });
        particlesRef.current[i].baseX = particlesRef.current[i].x;
        particlesRef.current[i].baseY = particlesRef.current[i].y;
      }
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    // Mouse tracking with smooth follow
    const handleMouseMove = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };
    
    const handleMouseLeave = () => {
      mouseRef.current.x = -1000;
      mouseRef.current.y = -1000;
    };
    
    if (!mobile) {
      window.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseleave', handleMouseLeave);
    }
    
    // Scroll velocity tracking
    let lastScrollY = window.scrollY;
    let lastTime = Date.now();
    
    const handleScroll = () => {
      const currentY = window.scrollY;
      const currentTime = Date.now();
      const deltaTime = currentTime - lastTime;
      
      if (deltaTime > 0) {
        const deltaScroll = currentY - lastScrollY;
        scrollRef.current.velocity = Math.abs(deltaScroll / deltaTime) * 1000;
        scrollRef.current.y = currentY;
      }
      
      lastScrollY = currentY;
      lastTime = currentTime;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      timeRef.current++;
      
      if (mobile) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }
      
      const particles = particlesRef.current;
      const mouse = mouseRef.current;
      const scroll = scrollRef.current;
      const time = timeRef.current;
      const intensity = intensityRef.current * getIntensity();
      
      // Smooth mouse target follow
      mouse.targetX += (mouse.x - mouse.targetX) * 0.08;
      mouse.targetY += (mouse.y - mouse.targetY) * 0.08;
      
      // Normalize scroll velocity (0-1)
      const scrollIntensity = Math.min(scroll.velocity / 2000, 1);
      
      // Influence radius changes with scroll velocity
      const baseInfluence = 180;
      const influenceRadius = baseInfluence + scrollIntensity * 60;
      
      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        
        // Distance to mouse
        const dx = p.x - mouse.targetX;
        const dy = p.y - mouse.targetY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Cursor repulsion (stronger when scrolling slowly)
        if (distance < influenceRadius && distance > 0) {
          const force = (influenceRadius - distance) / influenceRadius;
          const repelStrength = 2.2 * (1 - scrollIntensity * 0.5);
          const dirX = dx / distance;
          const dirY = dy / distance;
          p.vx += dirX * force * repelStrength * intensity;
          p.vy += dirY * force * repelStrength * intensity;
        }
        
        // Spring return to base
        const springStrength = 0.035 + scrollIntensity * 0.02;
        p.vx += (p.baseX - p.x) * springStrength;
        p.vy += (p.baseY - p.y) * springStrength;
        
        // Friction (less when scrolling fast)
        const friction = 0.88 - scrollIntensity * 0.1;
        p.vx *= friction;
        p.vy *= friction;
        
        // Deep wavy sinusoidal motion
        const waveX = Math.sin(p.offset + time * p.waveSpeed) * p.waveAmplitude * intensity;
        const waveY = Math.cos(p.offset * 1.3 + time * p.waveSpeed * 0.8) * p.waveAmplitude * 0.7 * intensity;
        
        // Scroll-induced drift (particles move opposite to scroll)
        const scrollDrift = scroll.velocity * 0.003 * p.depth * intensity;
        
        // Update position
        p.x += p.vx + waveX;
        p.y += p.vy + waveY - scrollDrift;
        
        // Soft boundary wrap
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) {
          p.y = canvas.height + 10;
          p.baseY = canvas.height + 10;
        }
        if (p.y > canvas.height + 10) {
          p.y = -10;
          p.baseY = -10;
        }
        
        // Draw particle with depth-based glow
        const glowSize = p.size * (1 + scrollIntensity * 0.5);
        const glowOpacity = p.opacity * intensity * (0.7 + scrollIntensity * 0.3);
        
        // Outer glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowSize * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${glowOpacity * 0.15})`;
        ctx.fill();
        
        // Core particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${glowOpacity})`;
        ctx.fill();
      }
      
      // Draw connections (less dense)
      const connectionDistance = 90 + scrollIntensity * 30;
      for (let i = 0; i < particles.length; i += 2) { // Skip every other for performance
        for (let j = i + 2; j < particles.length; j += 2) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.04 * intensity;
            ctx.strokeStyle = `rgba(0, 180, 200, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      
      // Decay scroll velocity
      scroll.velocity *= 0.95;
      
      rafRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Expose intensity update for external state transitions
    window.__vvsUpdateParticleIntensity = updateIntensity;
    
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', handleScroll);
      if (!mobile) {
        window.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
      cancelAnimationFrame(rafRef.current);
      delete window.__vvsUpdateParticleIntensity;
    };
  }, [updateIntensity]);
  
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(160deg, #050A14 0%, #060C18 50%, #050A14 100%)',
      }}
      aria-hidden="true"
    />
  );
}
